import { FC } from 'react';
import clsx from 'clsx';

interface Step {
  number: number;
  title: string;
}

interface StepIndicatorProps {
  currentStep: number;
  totalSteps: number;
  steps: Step[];
  className?: string;
  onStepClick?: (stepNumber: number) => void;
}

export const StepIndicator: FC<StepIndicatorProps> = ({
  currentStep,
  totalSteps: _totalSteps,
  steps,
  className,
  onStepClick,
}) => {
  const getStepStatus = (stepNumber: number): 'completed' | 'current' | 'pending' => {
    if (stepNumber < currentStep) return 'completed';
    if (stepNumber === currentStep) return 'current';
    return 'pending';
  };

  return (
    <nav aria-label="Form wizard steps" className={clsx('w-full py-6', className)}>
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const status = getStepStatus(step.number);
          const isLast = index === steps.length - 1;
          const isClickable = onStepClick && step.number !== currentStep;

          const StepContent = () => (
            <>
              {/* Circle */}
              <div
                className={clsx(
                  'w-10 h-10 rounded-full flex items-center justify-center',
                  'font-semibold text-sm transition-all duration-200',
                  {
                    // Completed
                    'bg-green-500 text-white': status === 'completed',
                    // Current
                    'bg-blue-600 text-white ring-4 ring-blue-100 dark:ring-blue-900':
                      status === 'current',
                    // Pending
                    'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400':
                      status === 'pending',
                  }
                )}
              >
                {status === 'completed' ? (
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  step.number
                )}
              </div>

              {/* Label */}
              <span
                className={clsx(
                  'mt-2 text-xs font-medium text-center whitespace-nowrap',
                  {
                    'text-green-600 dark:text-green-400': status === 'completed',
                    'text-blue-600 dark:text-blue-400': status === 'current',
                    'text-gray-500 dark:text-gray-400': status === 'pending',
                  }
                )}
              >
                {step.title}
              </span>
            </>
          );

          return (
            <div key={step.number} className="flex items-center flex-1">
              {/* Step circle and label - clickable or not */}
              {isClickable ? (
                <button
                  type="button"
                  onClick={() => onStepClick(step.number)}
                  aria-label={`Go to step ${step.number}: ${step.title}`}
                  className={clsx(
                    'flex flex-col items-center',
                    'cursor-pointer transition-all duration-200',
                    'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:z-10',
                    'hover:scale-105'
                  )}
                >
                  <StepContent />
                </button>
              ) : (
                <div
                  className={clsx(
                    'flex flex-col items-center',
                    {
                      'cursor-default': step.number === currentStep,
                    }
                  )}
                  aria-current={status === 'current' ? 'step' : undefined}
                >
                  <StepContent />
                </div>
              )}

              {/* Connector line */}
              {!isLast && (
                <div
                  className={clsx(
                    'flex-1 h-0.5 mx-2 transition-colors duration-200',
                    {
                      'bg-green-500': status === 'completed',
                      'bg-blue-600': status === 'current',
                      'bg-gray-200 dark:bg-gray-700': status === 'pending',
                    }
                  )}
                  style={{ minWidth: '40px' }}
                />
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
};
